import type { ComponentPropsWithoutRef, ElementType, ReactElement, ReactNode } from "react";
import { Fragment } from "react";

import { Trans, useTranslate } from "@similarweb/i18n-provider";

import { translate } from "app/filters/i18nFilter";
import type { TranslationReplacement, TranslationReplacements } from "app/filters/i18nFilter";
import SignalSelectionTooltip from "app/pages/sales-intelligence/sub-modules/signals/components/shared/SignalSelectionTooltip/SignalSelectionTooltip";
import {
    getPartialSelectionFromTranslatable,
    isFilterSingularLabel,
    isPartialSelectionTranslatable,
} from "app/pages/sales-intelligence/sub-modules/signals/models/services/filterSelectionNaming";
import { formatSelectionOverflowSuffix } from "app/pages/sales-intelligence/sub-modules/signals/utils/signalSelectionOverflow";
import type { Translatable } from "app/pages/sales-intelligence/types";

import { StyledStrongText } from "./styles";

type Props<E extends ElementType = "span"> = {
    translatable: Readonly<Translatable>;
    as?: E;
    emphasize?: boolean;
    overflowTooltipLabels?: readonly string[];
    showOverflowTooltip?: boolean;
} & Omit<ComponentPropsWithoutRef<E>, "as" | "children">;

const BOLD_COMPONENTS = { bold: <StyledStrongText /> };

const EMPHASIZED_SEGMENT_REPLACEMENT_KEYS = new Set([
    "adNetworks",
    "technologies",
    "topics",
    "countries",
    "country",
    "channelName",
    "metric",
    "period",
    "direction",
    "bucket",
]);

/** Internal marker for emphasized copy — never appears in real translations. */
const BOLD_MARKER = "\u0000";

const nestedSlotId = (replacementKey: string): string => `\uE000${replacementKey}\uE001`;

const isTranslatableReplacement = (value: unknown): value is Translatable =>
    typeof value === "object" && value !== null && typeof (value as Translatable).key === "string";

const toDisplayText = (value: TranslationReplacement): string => {
    if (typeof value === "string" && value.startsWith("si.")) {
        return translate(value);
    }

    return String(value);
};

const replaceNestedSlots = (text: string, nestedSlots: Readonly<Record<string, ReactNode>>): ReactNode => {
    const slotId = Object.keys(nestedSlots).find((candidate) => text.includes(candidate));

    if (!slotId) {
        return text;
    }

    const slotIndex = text.indexOf(slotId);

    return (
        <>
            {replaceNestedSlots(text.slice(0, slotIndex), nestedSlots)}
            {nestedSlots[slotId]}
            {replaceNestedSlots(text.slice(slotIndex + slotId.length), nestedSlots)}
        </>
    );
};

const parseSegmentCopy = (text: string, nestedSlots: Readonly<Record<string, ReactNode>>): ReactNode => {
    if (!text.includes(BOLD_MARKER)) {
        return replaceNestedSlots(text, nestedSlots);
    }

    return text.split(BOLD_MARKER).map((segment, index) => {
        const content = replaceNestedSlots(segment, nestedSlots);

        if (index % 2 === 1) {
            return <StyledStrongText key={index}>{content}</StyledStrongText>;
        }

        return <Fragment key={index}>{content}</Fragment>;
    });
};

const wrapEmphasizedContent = (content: ReactNode, emphasize: boolean): ReactNode =>
    emphasize ? <StyledStrongText>{content}</StyledStrongText> : content;

type PartialSelectionRenderOptions = {
    readonly emphasize?: boolean;
    readonly overflowTooltipLabels?: readonly string[];
    readonly showOverflowTooltip?: boolean;
};

const renderPartialSelectionContent = (
    translatable: Readonly<Translatable>,
    { emphasize = false, overflowTooltipLabels, showOverflowTooltip = false }: PartialSelectionRenderOptions,
): ReactNode => {
    const partialSelection = getPartialSelectionFromTranslatable(translatable);
    const overflowSuffix = formatSelectionOverflowSuffix(partialSelection?.remainingOptions ?? 0);
    const shouldShowOverflowTooltip =
        showOverflowTooltip && (overflowTooltipLabels?.length ?? 0) > 1 && Boolean(overflowSuffix);

    const selectionContent = (
        <StyledStrongText>
            {toDisplayText(partialSelection?.option ?? "")}
            {overflowSuffix}
        </StyledStrongText>
    );

    const content = shouldShowOverflowTooltip ? (
        <SignalSelectionTooltip
            labels={overflowTooltipLabels ?? []}
            inlineTextTrigger
            interaction="tooltip"
            triggerDataAutomation="signal-name-selection-tooltip-trigger"
        >
            {selectionContent}
        </SignalSelectionTooltip>
    ) : (
        selectionContent
    );

    return emphasize ? <StyledStrongText>{content}</StyledStrongText> : content;
};

type RenderTranslatableOptions = PartialSelectionRenderOptions;

const renderTranslatableContent = (
    translatable: Readonly<Translatable>,
    translateFn: typeof translate,
    options: RenderTranslatableOptions,
): ReactNode => {
    if (isPartialSelectionTranslatable(translatable)) {
        return renderPartialSelectionContent(translatable, options);
    }

    if (isFilterSingularLabel(translatable)) {
        return <StyledStrongText>{translatable.key}</StyledStrongText>;
    }

    const { key, replacements = {} } = translatable;
    const replacementKeys = Object.keys(replacements);

    if (replacementKeys.length === 0) {
        return (
            <Trans
                i18nKey={key}
                components={BOLD_COMPONENTS}
                t={translateFn}
            />
        );
    }

    const nestedSlots: Record<string, ReactNode> = {};
    const stringReplacements: TranslationReplacements = {};
    let needsStructuredRender = false;

    replacementKeys.forEach((replacementKey) => {
        const value = replacements[replacementKey];

        if (isTranslatableReplacement(value)) {
            const slotId = nestedSlotId(replacementKey);

            nestedSlots[slotId] = (
                <SignalsSegmentTranslatable
                    translatable={value}
                    emphasize={EMPHASIZED_SEGMENT_REPLACEMENT_KEYS.has(replacementKey)}
                    overflowTooltipLabels={options.overflowTooltipLabels}
                    showOverflowTooltip={options.showOverflowTooltip}
                />
            );
            stringReplacements[replacementKey] = slotId;
            needsStructuredRender = true;

            return;
        }

        if (EMPHASIZED_SEGMENT_REPLACEMENT_KEYS.has(replacementKey)) {
            stringReplacements[replacementKey] =
                `${BOLD_MARKER}${toDisplayText(value as TranslationReplacement)}${BOLD_MARKER}`;
            needsStructuredRender = true;

            return;
        }

        stringReplacements[replacementKey] = value as TranslationReplacement;
    });

    if (!needsStructuredRender) {
        return (
            <Trans
                i18nKey={key}
                values={stringReplacements}
                components={BOLD_COMPONENTS}
                t={translateFn}
            />
        );
    }

    return parseSegmentCopy(translate(key, stringReplacements), nestedSlots);
};

const SignalsSegmentTranslatable = <E extends ElementType = "span">({
    translatable,
    as,
    emphasize = false,
    overflowTooltipLabels,
    showOverflowTooltip = false,
    ...elementProps
}: Props<E>): ReactElement => {
    const { translate: translateFromContext } = useTranslate();
    const Component = as ?? "span";

    return (
        <Component {...elementProps}>
            {wrapEmphasizedContent(
                renderTranslatableContent(translatable, translateFromContext, {
                    emphasize,
                    overflowTooltipLabels,
                    showOverflowTooltip,
                }),
                emphasize && !isPartialSelectionTranslatable(translatable),
            )}
        </Component>
    );
};

export default SignalsSegmentTranslatable;
